import { useCallback, useMemo } from "react";
import { useApolloClient, useMutation } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";

import {
  CreateCustomFieldDocument,
  type CreateCustomFieldInput,
  CreateCustomFieldOptionDocument,
  DeleteCustomFieldDocument,
  DuplicateCustomFieldDocument,
  GetIssueTypeDocument,
  UpdateCustomFieldDocument,
  type UpdateCustomFieldInput,
  UpdateIssueTypeDocument,
} from "#app/lib/data";
import { useAnalytics, AnalyticsContext, SettingsEvent, trackEvent } from "#app/lib/analytics";
import { resultMessage, toastMutationError } from "#app/lib/utils/toast-errors";
import type { MutationFeedbackOptions } from "#app/lib/hooks/use-team-mutations";
import { usePermission } from "#app/lib/hooks/use-permission";
import type { CustomFieldDef } from "#app/components/custom-fields/types";
import type { OnCreateOption } from "#app/components/custom-fields/option-picker";
import {
  getOptionMetas,
  type OptionColorChoice,
  type OptionMeta,
} from "#app/components/custom-fields/option-visuals";

export function useCustomFieldMutations() {
  const client = useApolloClient();
  const { track } = useAnalytics();
  const [createCustomFieldMutation] = useMutation(CreateCustomFieldDocument);
  const [updateCustomFieldMutation] = useMutation(UpdateCustomFieldDocument);
  const [deleteCustomFieldMutation] = useMutation(DeleteCustomFieldDocument);
  const [duplicateCustomFieldMutation] = useMutation(DuplicateCustomFieldDocument);

  const refetchList = useCallback(
    () => client.refetchQueries({ include: ["CustomFieldsListData"] }),
    [client],
  );

  const createCustomField = useCallback(
    async (input: CreateCustomFieldInput) => {
      try {
        const { data } = await createCustomFieldMutation({
          variables: { input },
          refetchQueries: ["CustomFieldsListData"],
        });
        const result = data?.createCustomField;
        if (result?.__typename === "CustomField") track({ context: AnalyticsContext.Settings, event: SettingsEvent.Create, label: "custom_field", data: { fieldType: result && "type" in result ? result.type : undefined } });
        // A refusal (a name another field holds) is a result, not a throw.
        if (result?.__typename !== "CustomField") {
          toastManager.add({ title: resultMessage(result) ?? "Failed to create custom field", type: "error" });
          return null;
        }
        return result;
      } catch (err) {
        toastMutationError(err, "Failed to create custom field");
        return null;
      }
    },
    [createCustomFieldMutation, track],
  );

  const updateCustomField = useCallback(
    async (id: string, input: UpdateCustomFieldInput) => {
      try {
        const { data } = await updateCustomFieldMutation({
          variables: { id, input },
        });
        const result = data?.updateCustomField;
        if (result?.__typename === "CustomField") track({ context: AnalyticsContext.Settings, event: SettingsEvent.Update, label: "custom_field", data: { fieldType: result && "type" in result ? result.type : undefined } });
        // A refusal (a name another field holds) is a result, not a throw.
        if (result?.__typename !== "CustomField") {
          toastManager.add({ title: resultMessage(result) ?? "Failed to update custom field", type: "error" });
          return null;
        }
        return result;
      } catch (err) {
        toastMutationError(err, "Failed to update custom field");
        return null;
      }
    },
    [updateCustomFieldMutation, track],
  );

  // `silent` is the bulk contract (see `MutationFeedbackOptions`): no error
  // toast, and a refusal or a request error rejects so the batch reports once.
  const deleteCustomField = useCallback(
    async (id: string, options?: MutationFeedbackOptions) => {
      try {
        const { data } = await deleteCustomFieldMutation({
          variables: { id },
        });
        const result = data?.deleteCustomField;
        // A refusal (a field already gone) is a result, not a throw.
        if (result?.__typename !== "CustomField") {
          const message = resultMessage(result) ?? "Failed to delete custom field";
          if (options?.silent) throw new Error(message);
          toastManager.add({ title: message, type: "error" });
          return null;
        }
        track({ context: AnalyticsContext.Settings, event: SettingsEvent.Delete, label: "custom_field" });
        return result;
      } catch (err) {
        if (options?.silent) throw err;
        toastMutationError(err, "Failed to delete custom field");
        return null;
      }
    },
    [deleteCustomFieldMutation, track],
  );

  const duplicateCustomField = useCallback(
    async (id: string, options?: MutationFeedbackOptions) => {
      try {
        const { data } = await duplicateCustomFieldMutation({
          variables: { id },
          refetchQueries: ["CustomFieldsListData"],
        });
        const result = data?.duplicateCustomField;
        if (result?.__typename === "CustomField") track({ context: AnalyticsContext.Settings, event: SettingsEvent.Duplicate, label: "custom_field", data: { fieldType: result && "type" in result ? result.type : undefined } });
        // A refusal (a name another field holds) is a result, not a throw.
        if (result?.__typename !== "CustomField") {
          const message = resultMessage(result) ?? "Failed to duplicate custom field";
          if (options?.silent) throw new Error(message);
          toastManager.add({ title: message, type: "error" });
          return null;
        }
        return result;
      } catch (err) {
        if (options?.silent) throw err;
        toastMutationError(err, "Failed to duplicate custom field");
        return null;
      }
    },
    [duplicateCustomFieldMutation, track],
  );

  // Appends a field to each given project type, preserving that type's
  // existing fields (updateIssueType replaces the whole array). Best-effort
  // per type — a failure on one doesn't abort the rest; skips types that
  // already carry the field.
  const associateIssueTypes = useCallback(
    async (customFieldId: string, issueTypeIds: string[]) => {
      for (const issueTypeId of issueTypeIds) {
        try {
          const { data } = await client.query({
            query: GetIssueTypeDocument,
            variables: { id: issueTypeId },
            fetchPolicy: "cache-first",
          });
          const existing = (data?.issueType?.fields ?? []).map((f) => ({
            customFieldId: f.customFieldId,
            required: f.required,
          }));
          if (existing.some((f) => f.customFieldId === customFieldId)) continue;
          await client.mutate({
            mutation: UpdateIssueTypeDocument,
            variables: {
              id: issueTypeId,
              input: { fields: [...existing, { customFieldId, required: false }] },
            },
          });
        } catch (err) {
          toastMutationError(err, "Failed to add field to a Project type");
        }
      }
    },
    [client],
  );

  return {
    createCustomField,
    updateCustomField,
    deleteCustomField,
    duplicateCustomField,
    associateIssueTypes,
    refetchList,
  };
}

// Inline option creation from a value picker — create.items-gated server-side
// (NOT custom_fields.manage), so it lives outside useCustomFieldMutations'
// manage vocabulary. Quiet on success: the created option applies to the item
// immediately, which is its own confirmation. Returns the updated field (the
// normalized cache entity refreshes every option list) or null on failure.
export function useCreateCustomFieldOption() {
  const [createOptionMutation] = useMutation(CreateCustomFieldOptionDocument);

  return useCallback(
    async (customFieldId: string, label: string, color?: string | null) => {
      try {
        const { data } = await createOptionMutation({
          variables: {
            customFieldId,
            input: { label, color: color ?? null },
          },
        });
        const result = data?.createCustomFieldOption;
        if (result && result.__typename !== "CustomField") {
          toastManager.add({
            title: result.message || "Failed to create option",
            type: "error",
          });
          return null;
        }
        if (result) {
          trackEvent({
            context: AnalyticsContext.Settings,
            event: SettingsEvent.Create,
            label: "custom_field_option",
            data: { hasColor: color != null },
          });
        }
        return result ?? null;
      } catch (err) {
        toastMutationError(err, "Failed to create option");
        return null;
      }
    },
    [createOptionMutation],
  );
}

// ---------------------------------------------------------------------------
// In-picker option management (rename/recolor/delete/reorder) — managers
// only; callers gate on custom_fields.manage since updateCustomField is
// permission-gated. There is no per-option mutation: every action is a
// read-modify-write of the field's full config.options array, and the
// mutation returns the full CustomField, so Apollo's normalized field def
// (and every picker reading config.options) updates in place — no refetch.
// Quiet on success: the picker shows the result immediately.
// ---------------------------------------------------------------------------

export type OptionActions = {
  /** Rename/recolor one option. The option's `value` is preserved on rename —
   *  applied issue values key on it, and the service round-trips it untouched
   *  — so renames never orphan applied values. */
  updateOption: (
    field: CustomFieldDef,
    value: string,
    patch: { label?: string; color?: OptionColorChoice },
  ) => Promise<void>;
  /** Remove one option from the field. Issue values still carrying it render
   *  the grey fallback chip; the service prunes it from the stored default. */
  deleteOption: (field: CustomFieldDef, value: string) => Promise<void>;
  /** Persist a new option order after a drag (manual sort only). */
  reorderOptions: (field: CustomFieldDef, options: OptionMeta[]) => Promise<void>;
};

export function useOptionActions(): OptionActions {
  const [updateCustomFieldMutation] = useMutation(UpdateCustomFieldDocument);

  /** Replace the field's full option list (shared by reorder/edit/delete). */
  const persistOptions = useCallback(
    async (field: CustomFieldDef, options: OptionMeta[], errorMessage: string) => {
      try {
        const { data } = await updateCustomFieldMutation({
          variables: {
            id: field.customFieldId,
            input: {
              config: {
                options: options.map((o) => ({
                  label: o.label,
                  value: o.value,
                  color: o.color,
                })),
              },
            },
          },
        });
        const result = data?.updateCustomField;
        if (result && result.__typename !== "CustomField") {
          toastManager.add({
            title: result.message || errorMessage,
            type: "error",
          });
        }
      } catch (err) {
        toastMutationError(err, errorMessage);
      }
    },
    [updateCustomFieldMutation],
  );

  const updateOption = useCallback(
    (
      field: CustomFieldDef,
      value: string,
      patch: { label?: string; color?: OptionColorChoice },
    ) =>
      persistOptions(
        field,
        getOptionMetas(field).map((o) => (o.value === value ? { ...o, ...patch } : o)),
        "Failed to update option",
      ),
    [persistOptions],
  );

  const deleteOption = useCallback(
    (field: CustomFieldDef, value: string) =>
      persistOptions(
        field,
        getOptionMetas(field).filter((o) => o.value !== value),
        "Failed to delete option",
      ),
    [persistOptions],
  );

  const reorderOptions = useCallback(
    (field: CustomFieldDef, options: OptionMeta[]) =>
      persistOptions(field, options, "Failed to reorder options"),
    [persistOptions],
  );

  return useMemo(
    () => ({ updateOption, deleteOption, reorderOptions }),
    [updateOption, deleteOption, reorderOptions],
  );
}

/** The two picker extras every option-value host wires the same way: the
 *  create.items-gated inline create row (the picker gates the affordance
 *  itself) and manager-only option editing, present only with
 *  custom_fields.manage — absent, the picker renders no manage chrome. */
export function useOptionPickerOps(): {
  onCreateOption: OnCreateOption;
  optionActions: OptionActions | undefined;
} {
  const onCreateOption = useCreateCustomFieldOption();
  const canManageCustomFields = usePermission("custom_fields.manage");
  const optionActionsRaw = useOptionActions();
  return useMemo(
    () => ({
      onCreateOption,
      optionActions: canManageCustomFields ? optionActionsRaw : undefined,
    }),
    [onCreateOption, canManageCustomFields, optionActionsRaw],
  );
}
